import type { RequiredPermissions } from 'types/Permissions';

import { CustomAccessLevel } from 'enums/extendedAccessLevel';

import { AccessLevel, ResourceType, UserScope } from 'store/client/graphql';

/**
 * Priority weights for permission level comparison. The higher the integer, the higher
 * the access level. A user with a higher-level access to a resource automatically
 * inherits all the lower-level access levels.
 *
 * NO_ACCESS = -1 ensures it is below all real access levels (BASIC=0, ADVANCED=1, FULL=2),
 * so a user assigned NO_ACCESS on a resource is always denied regardless of the required level.
 */
export const LevelPriority = {
  [AccessLevel.ADVANCED]: 1,
  [AccessLevel.BASIC]: 0,
  [AccessLevel.FULL]: 2,
  [CustomAccessLevel.NO_ACCESS]: -1,
};

/**
 * Any rooftops access. The read-level bar for rooftop-scoped surfaces; write actions gate on ADVANCED.
 *
 * The annotation is required, not stylistic: it resolves to the tuple `NonEmptyArray`, which a bare
 * array literal does not infer. `NonNullable` is deliberate — a null permission list means "allow
 * everyone" to every gate, which this constant never does.
 */
export const ROOFTOPS_BASIC_PERMISSIONS: NonNullable<RequiredPermissions> = [
  { level: AccessLevel.BASIC, resource: ResourceType.ROOFTOPS },
];

export const whiteLabelScopes = [UserScope.GLOBAL, UserScope.WHITE_LABEL];
export const nonWhiteLabelScopes = [UserScope.FULL_GROUP, UserScope.PARTIAL_GROUP];
export const fullGroupOrAboveScopes = [UserScope.FULL_GROUP, ...whiteLabelScopes];

export const hasWhiteLabelScopedAccess = (scope: UserScope) => whiteLabelScopes.includes(scope);

const scopeOrder: UserScope[] = [
  UserScope.PARTIAL_GROUP,
  UserScope.FULL_GROUP,
  UserScope.WHITE_LABEL,
  UserScope.GLOBAL,
];

export const hasMinimumScope = (userScope: UserScope, minimumScope: UserScope) => {
  const minimumScopeIndex = scopeOrder.indexOf(minimumScope);

  return scopeOrder.indexOf(userScope) >= minimumScopeIndex;
};

type AuthorizedCallbackParameters<TBoolean extends boolean, TArgs extends any[]> = {
  cb: (...args: TArgs) => void;
  isAuth: TBoolean;
};

export function authorizedCallback<TBoolean extends boolean, TArgs extends any[]>(
  parameters: AuthorizedCallbackParameters<TBoolean, TArgs>
): TBoolean extends true ? (...args: TArgs) => void : undefined;
export function authorizedCallback<TBoolean extends boolean, TArgs extends any[], TFArgs extends any[]>(
  parameters: AuthorizedCallbackParameters<TBoolean, TArgs> & {
    /**
     * Fallback to return when user is not authorized
     */
    fallback: (...args: TFArgs) => void;
  }
): TBoolean extends true ? (...args: TArgs) => void : (...args: TFArgs) => void;
export function authorizedCallback(parameters) {
  return parameters.isAuth ? parameters.cb : parameters.fallback || undefined;
}
